<template>
    <v-dialog :value="value" max-width="500" persistent @input="$emit('input', $event)">
        <v-card>
            <v-card-title class="py-3 px-4">
                <span class="text-h6 font-weight-bold">Add leave</span>
                <v-spacer />
                <v-btn icon :disabled="saving" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4">
                <div v-if="user" class="d-flex align-center mb-3">
                    <v-avatar size="40" color="primary" class="mr-3">
                        <span class="white--text">{{ initialsOf(user) }}</span>
                    </v-avatar>
                    <div>
                        <div class="font-weight-bold">{{ displayName }}</div>
                        <div class="text-caption grey--text">Approved straight away — no approval step, no email.</div>
                    </div>
                </div>
                <template v-else>
                    <v-autocomplete
                        v-model="personId"
                        :items="peopleItems"
                        item-text="label"
                        item-value="id"
                        label="Who is it for?"
                        prepend-inner-icon="mdi-account"
                        outlined
                        dense
                        hide-details
                        class="mb-1"
                    />
                    <div class="text-caption grey--text mb-3">Approved straight away — no approval step, no email.</div>
                </template>

                <v-select
                    v-model="typeId"
                    :items="leaveTypes"
                    item-text="name"
                    item-value="id"
                    label="Leave type"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                />

                <v-btn-toggle v-model="durationType" mandatory dense color="primary">
                    <v-btn value="full" small class="text-none">Full day(s)</v-btn>
                    <v-btn value="half" small class="text-none">Half day</v-btn>
                </v-btn-toggle>

                <v-btn-toggle v-if="durationType === 'half'" v-model="halfDayPeriod" mandatory dense color="orange" class="ml-2">
                    <v-btn value="AM" small class="text-none">Morning</v-btn>
                    <v-btn value="PM" small class="text-none">Afternoon</v-btn>
                </v-btn-toggle>

                <v-row dense class="mt-3">
                    <v-col cols="12" :sm="durationType === 'full' ? 6 : 12">
                        <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y min-width="auto">
                            <template #activator="{ on, attrs }">
                                <v-text-field
                                    :value="fromDate"
                                    :label="durationType === 'full' ? 'From' : 'Date'"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker v-model="fromDate" first-day-of-week="1" @input="onFromPicked" />
                        </v-menu>
                    </v-col>
                    <v-col v-if="durationType === 'full'" cols="12" sm="6">
                        <v-menu v-model="toMenu" :close-on-content-click="false" offset-y min-width="auto">
                            <template #activator="{ on, attrs }">
                                <v-text-field
                                    :value="toDate"
                                    label="To (last day off)"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker v-model="toDate" :min="fromDate" first-day-of-week="1" @input="toMenu = false" />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-alert v-if="duration" dense outlined type="info" class="mt-3 mb-0">
                    <span class="text-caption"><strong>Duration:</strong> {{ duration }}</span>
                </v-alert>

                <v-textarea
                    v-model="note"
                    :label="'Note for ' + (firstName || 'them') + ' (optional)'"
                    :hint="(firstName || 'They') + ' can see this note on their own calendar'"
                    persistent-hint
                    outlined
                    dense
                    rows="2"
                    class="mt-3"
                />
                <v-textarea
                    v-if="canManagerNote"
                    v-model="managerNote"
                    label="Private manager note (optional)"
                    hint="Only managers see this — not the employee"
                    persistent-hint
                    prepend-inner-icon="mdi-eye-off-outline"
                    outlined
                    dense
                    rows="2"
                    class="mt-3"
                />
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-3">
                <v-spacer />
                <v-btn text :disabled="saving" @click="$emit('input', false)">Cancel</v-btn>
                <v-btn color="primary" depressed class="text-none" :disabled="!canSave" :loading="saving" @click="save">
                    <v-icon small left>mdi-check</v-icon>
                    Add leave
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { fullName, initials } from './leaveUtils';

// A manager logs leave for someone straight onto their calendar. The panel posts it
// (POST api/leave/manual/{user}) and refreshes the calendar afterwards.
export default {
    name: 'ManagerLeaveDialog',
    props: {
        value: { type: Boolean, default: false },
        // fixed person (their own calendar is open); leave null to pick from `people`
        user: { type: Object, default: null },
        people: { type: Array, default: () => [] },
        // the calendar day that was clicked (YYYY-MM-DD), when there was one
        date: { type: String, default: null },
        leaveTypes: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        // the calendar the manager is in; the private note lives there ({ company_id } or { role_id })
        noteScope: { type: Object, default: null },
    },
    data() {
        return {
            typeId: null,
            personId: null,
            durationType: 'full',
            halfDayPeriod: 'AM',
            fromDate: null,
            toDate: null,
            fromMenu: false,
            toMenu: false,
            note: '',
            managerNote: '',
        };
    },
    computed: {
        displayName() {
            return this.user ? fullName(this.user) : '';
        },
        peopleItems() {
            return this.people.map((u) => ({ id: Number(u.id), label: fullName(u) }));
        },
        canManagerNote() {
            return !!this.noteScope;
        },
        firstName() {
            const u = this.user || this.people.find((p) => Number(p.id) === Number(this.personId));
            return u ? String(fullName(u)).split(' ')[0] : '';
        },
        targetId() {
            return this.user ? Number(this.user.id) : this.personId;
        },
        dayCount() {
            if (!this.fromDate) return null;
            if (this.durationType === 'half') return 0.5;
            if (!this.toDate) return null;
            return this.$date(this.toDate).diff(this.$date(this.fromDate), 'day') + 1;
        },
        duration() {
            if (this.dayCount === null) return '';
            if (this.durationType === 'half') return `0.5 day (${this.halfDayPeriod === 'PM' ? 'afternoon' : 'morning'})`;
            return `${this.dayCount} day${this.dayCount === 1 ? '' : 's'}`;
        },
        canSave() {
            return !!this.targetId && !!this.typeId && !!this.fromDate && (this.durationType === 'half' || (!!this.toDate && this.toDate >= this.fromDate));
        },
    },
    watch: {
        value(open) {
            if (!open) return;
            const start = this.date || this.$date().format('YYYY-MM-DD');
            this.fromDate = start;
            this.toDate = start;
            this.durationType = 'full';
            this.halfDayPeriod = 'AM';
            this.note = '';
            this.managerNote = '';
            this.personId = null;
            this.typeId = this.typeId || this.defaultTypeId();
        },
    },
    methods: {
        initialsOf(u) {
            return initials(fullName(u));
        },
        // sick leave is what comes up most in conversation, so offer it first when it exists
        defaultTypeId() {
            const sick = this.leaveTypes.find((t) => /sick/i.test(t.name || ''));
            const first = sick || this.leaveTypes[0];
            return first ? first.id : null;
        },
        onFromPicked() {
            this.fromMenu = false;
            if (!this.toDate || this.toDate < this.fromDate) this.toDate = this.fromDate;
        },
        save() {
            if (!this.canSave) return;
            const half = this.durationType === 'half';
            this.$emit('save', {
                user_id: this.targetId,
                leave_type_id: Number(this.typeId),
                from: this.fromDate,
                to: half ? this.fromDate : this.toDate,
                is_half_day: half,
                half_day_period: half ? this.halfDayPeriod : null,
                note: (this.note || '').trim() || null,
                ...(this.canManagerNote && (this.managerNote || '').trim()
                    ? { manager_note: this.managerNote.trim(), ...this.noteScope }
                    : {}),
            });
        },
    },
};
</script>
